import { useState, useEffect, useCallback } from 'react';
import {
  getTasks,
  Task,
  getKeywords,
  Tag,
} from '@/app/task-bank/public-actions';
import { useAuth } from '@/context/auth-context';
import { Task1Type, VisualType, QuestionType } from '@/types/task-bank';
import { useDebounce } from '@/hooks/use-debounce';

interface UseTaskSelectionProps {
  open: boolean;
  taskType: 'task1' | 'task2';
}

export function useTaskSelection({ open, taskType }: UseTaskSelectionProps) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  const [lastTaskId, setLastTaskId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [task1TypeFilter, setTask1TypeFilter] = useState<Task1Type | 'all'>(
    'all'
  );
  const [visualTypeFilter, setVisualTypeFilter] = useState<VisualType | 'all'>(
    'all'
  );
  const [questionTypeFilter, setQuestionTypeFilter] = useState<
    QuestionType | 'all'
  >('all');

  const [keywords, setKeywords] = useState<Tag[]>([]);
  const [selectedKeywordId, setSelectedKeywordId] = useState<string | null>(
    null
  );
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const [keywordSearch, setKeywordSearch] = useState('');

  const fetchKeywords = useCallback(async () => {
    try {
      const fetchedKeywords = await getKeywords();
      setKeywords(fetchedKeywords);
    } catch (err) {
      console.error('Error fetching keywords:', err);
    }
  }, []);

  const fetchTasks = useCallback(
    async (startAfterId?: string) => {
      if (startAfterId) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setTasks([]);
        setLastTaskId(null);
        setHasMore(true);
      }

      setError(null);

      try {
        const options = {
          taskType,
          keywordId: selectedKeywordId || undefined,
          limit: 10,
          startAfterId,
        };

        const {
          tasks: fetchedTasks,
          lastTaskId: lastId,
          hasMore: more,
        } = await getTasks(options);

        let filteredTasks = fetchedTasks;

        if (debouncedSearch.trim()) {
          const query = debouncedSearch.toLowerCase().trim();
          filteredTasks = filteredTasks.filter((task) =>
            task.prompt.toLowerCase().includes(query)
          );
        }

        if (taskType === 'task1') {
          if (task1TypeFilter !== 'all') {
            filteredTasks = filteredTasks.filter(
              (task) => task.task1Type === task1TypeFilter
            );
          }

          if (visualTypeFilter !== 'all') {
            filteredTasks = filteredTasks.filter(
              (task) => task.visualType === visualTypeFilter
            );
          }
        } else if (taskType === 'task2') {
          if (questionTypeFilter !== 'all') {
            filteredTasks = filteredTasks.filter(
              (task) => task.questionType === questionTypeFilter
            );
          }
        }

        if (startAfterId) {
          setTasks((prev) => [...prev, ...filteredTasks]);
        } else {
          setTasks(filteredTasks);
        }

        setLastTaskId(lastId);
        setHasMore(more && filteredTasks.length > 0);
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [
      taskType,
      selectedKeywordId,
      debouncedSearch,
      task1TypeFilter,
      visualTypeFilter,
      questionTypeFilter,
    ]
  );

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || !lastTaskId) return;
    fetchTasks(lastTaskId);
  }, [hasMore, isLoadingMore, lastTaskId, fetchTasks]);

  const toggleKeyword = useCallback((id: string) => {
    setSelectedKeywordId((prev) => (prev === id ? null : id));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    if (taskType === 'task2') {
      setSelectedKeywordId(null);
    }
    if (taskType === 'task1') {
      setTask1TypeFilter('all');
      setVisualTypeFilter('all');
    } else {
      setQuestionTypeFilter('all');
    }
  }, [taskType]);

  useEffect(() => {
    if (open && user) {
      setSearchQuery('');
      setTask1TypeFilter('all');
      setVisualTypeFilter('all');
      setQuestionTypeFilter('all');
      setSelectedKeywordId(null);
      setKeywordSearch('');
      fetchTasks();
      if (taskType === 'task2') {
        fetchKeywords();
      }
    }
  }, [open, user, taskType, fetchTasks, fetchKeywords]);

  useEffect(() => {
    if (open && user) {
      fetchTasks();
    }
  }, [
    debouncedSearch,
    selectedKeywordId,
    task1TypeFilter,
    visualTypeFilter,
    questionTypeFilter,
    taskType,
    open,
    user,
    fetchTasks,
  ]);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        target.scrollHeight - target.scrollTop - target.clientHeight < 100 &&
        !isLoading &&
        hasMore
      ) {
        loadMore();
      }
    };
    const scrollArea = document.querySelector(
      '[data-radix-scroll-area-viewport]'
    );
    if (scrollArea) {
      scrollArea.addEventListener('scroll', handleScroll);
      return () => scrollArea.removeEventListener('scroll', handleScroll);
    }
  }, [loadMore, isLoading, hasMore]);

  return {
    tasks,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    task1TypeFilter,
    setTask1TypeFilter,
    visualTypeFilter,
    setVisualTypeFilter,
    questionTypeFilter,
    setQuestionTypeFilter,
    keywords,
    selectedKeywordId,
    toggleKeyword,
    showAllKeywords,
    setShowAllKeywords,
    keywordSearch,
    setKeywordSearch,
    isLoadingMore,
    hasMore,
    clearFilters,
  };
}
